/**
 * Per-form marketing tracking helpers.
 *
 * A form can carry an owner-supplied Meta (Facebook) Pixel ID and/or a Google
 * Tag Manager container ID, set under Form Settings → Tracking. These are the
 * *form owner's* tags — distinct from Formsuite's own GTM/Pixel injected by the
 * app's root layout. They are injected only on the public form page (see
 * `FormTrackingScripts`), which fires a `PageView` on load; the conversion
 * (`Lead`) event is fired separately on submission via `fireFormConversion`.
 *
 * The settings field is forgiving: an owner may paste either a bare ID
 * (`1234567890`, `GTM-ABC123`) or a full tag snippet copied from Meta/Google.
 * We extract the ID from whichever they provide.
 */

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    // Must match the existing augmentation in `@plura/shared/track` exactly
    // (declaration merging requires identical types for shared properties).
    dataLayer?: Record<string, any>[];
  }
}

/** Pull a Meta Pixel ID (a 10–20 digit number) out of a bare ID or pasted snippet. */
export const extractMetaPixelId = (raw?: string | null): string | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  // `fbq('init', '1234567890')`-style paste.
  const initMatch = trimmed.match(/init['"\s,]+(\d{10,20})/);
  if (initMatch) return initMatch[1];
  // Bare numeric id.
  if (/^\d{10,20}$/.test(trimmed)) return trimmed;
  // Fallback: first long digit run inside a larger paste.
  const anyDigits = trimmed.match(/\b(\d{10,20})\b/);
  return anyDigits ? anyDigits[1] : null;
};

/** Pull a GTM container ID (`GTM-XXXXXX`) out of a bare ID or pasted snippet. */
export const extractGtmId = (raw?: string | null): string | null => {
  if (!raw) return null;
  const match = raw.toUpperCase().match(/GTM-[A-Z0-9]+/);
  return match ? match[0] : null;
};

/**
 * Fire a conversion signal on form submission to the form owner's tags.
 *
 * No-op on the server, and when nothing is configured. `fbq`/`dataLayer` are
 * created by `FormTrackingScripts`; if the form has no pixel/GTM these globals
 * simply don't exist (or aren't the owner's) and we skip. We gate the GTM push
 * on the owner actually having a GTM id so we don't pollute Formsuite's own
 * `dataLayer` (present on every page via the root layout) with form events.
 */
export const fireFormConversion = (args: {
  metaPixel?: string | null;
  googleTagManager?: string | null;
}): void => {
  if (typeof window === 'undefined') return;

  const pixelId = extractMetaPixelId(args.metaPixel);
  if (pixelId && typeof window.fbq === 'function') {
    // `trackSingle` targets *this* pixel only, so the event never leaks to
    // another pixel that may be initialised on the same page.
    window.fbq('trackSingle', pixelId, 'Lead');
  }

  if (extractGtmId(args.googleTagManager) && Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: 'form_submit' });
  }
};
